/**
 * Tarjeta de inmueble para la vista «Tarjetas» de Propiedades.
 *
 * Responde de un vistazo a lo que se mira al buscar un inmueble: cuál es (foto y nombre),
 * dónde está, cómo va de lleno y cuánto da. Las habitaciones se pintan como PUNTOS —relleno,
 * ocupada; hueco, libre— porque se leen sin leer: cuatro puntos llenos es «completo» antes de
 * llegar al texto.
 *
 * Toda la tarjeta es un enlace a la ficha, como la fila de la tabla. Sin botones dentro: dos
 * destinos en la misma superficie obligan a apuntar, y aquí solo hay uno.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PropertyListRow } from '@naar/shared';
import { formatEur, formatPercent } from '../../lib/format';
import styles from './PropertyCard.module.css';

/** A partir de aquí los puntos dejan de leerse de un vistazo: el resto va como «+N». */
const MAX_DOTS = 12;

export type Tone = 'ok' | 'warn' | 'neutral';

/** Etiqueta de estado del inmueble. La comparten la tarjeta y la ficha resumen del mapa. */
export function estado(row: PropertyListRow): { label: string; tone: Tone } {
  if (row.status === 'vendida') return { label: 'Vendida', tone: 'neutral' };
  if (row.status === 'en_reforma') return { label: 'En reforma', tone: 'neutral' };
  if (row.units === 0) return { label: 'Sin habitaciones', tone: 'neutral' };
  if (row.freeUnits > 0) {
    return { label: `${row.freeUnits} libre${row.freeUnits === 1 ? '' : 's'}`, tone: 'warn' };
  }
  return { label: 'Completo', tone: 'ok' };
}

export function ocupacion(row: PropertyListRow): string {
  if (row.units === 0) return 'Sin habitaciones todavía';
  return `${row.occupiedUnits} de ${row.units} ${row.units === 1 ? 'habitación ocupada' : 'habitaciones ocupadas'}`;
}

/** Tejado y puerta cambian de tono según el inmueble, para que dos sin foto no sean gemelos. */
const TONOS = ['#8D7E6C', '#6B5D4C', '#A8977F', '#5C4F40'];
function tono(id: string): string {
  let suma = 0;
  for (const letra of id) suma = (suma + letra.charCodeAt(0)) % 997;
  return TONOS[suma % TONOS.length]!;
}

/** Fachada dibujada: el hueco de la foto cuando el inmueble aún no tiene ninguna. */
function Fachada({ color }: { color: string }) {
  return (
    <svg className={styles.facade} viewBox="0 0 320 200" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
      <rect x="0" y="168" width="320" height="32" fill="#E2D8C9" />
      <rect x="70" y="44" width="180" height="128" rx="4" fill="#D8CBB8" />
      <rect x="62" y="36" width="196" height="12" rx="3" fill={color} />
      <g fill="#F7F3EC">
        <rect x="92" y="62" width="26" height="30" rx="3" />
        <rect x="147" y="62" width="26" height="30" rx="3" />
        <rect x="202" y="62" width="26" height="30" rx="3" />
        <rect x="92" y="108" width="26" height="30" rx="3" />
        <rect x="202" y="108" width="26" height="30" rx="3" />
      </g>
      <rect x="146" y="118" width="28" height="54" rx="3" fill={color} />
    </svg>
  );
}

export function PropertyCard({ row }: { row: PropertyListRow }) {
  /*
   * La URL de la portada puede venir firmada y aun así dar 404 (fila sin fichero en el almacén,
   * o caducada con la pantalla abierta): entonces se cae a la fachada en vez de dejar el icono
   * de imagen rota.
   */
  const [rota, setRota] = useState(false);
  const foto = row.cover?.url && !rota ? row.cover.url : null;
  const chip = estado(row);
  const lugar = row.province && row.province !== row.city ? `${row.city} · ${row.province}` : row.city;
  const visibles = Math.min(row.units, MAX_DOTS);

  return (
    <Link to={`/propiedades/${row.id}`} className={styles.card}>
      <div className={styles.visual}>
        {foto ? (
          // Decorativa: el nombre del inmueble ya está escrito justo debajo.
          <img className={styles.photo} src={foto} alt="" loading="lazy" onError={() => setRota(true)} />
        ) : (
          <Fachada color={tono(row.id)} />
        )}
        <span className={`${styles.chip} ${styles[chip.tone]}`}>{chip.label}</span>
      </div>

      <div className={styles.body}>
        <div className={styles.titles}>
          <span className={styles.name}>{row.name}</span>
          <span className={styles.place}>{lugar}</span>
        </div>

        <div className={styles.rooms}>
          {row.units > 0 && (
            <span className={styles.dots} aria-hidden="true">
              {Array.from({ length: visibles }, (_, index) => (
                <span
                  key={index}
                  className={index < row.occupiedUnits ? styles.dotOn : styles.dotOff}
                />
              ))}
              {row.units > MAX_DOTS && <span className={styles.more}>+{row.units - MAX_DOTS}</span>}
            </span>
          )}
          <span className={styles.roomsLabel}>{ocupacion(row)}</span>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Renta al mes</span>
            <span className={styles.statValue}>{formatEur(row.monthlyRent)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Rentabilidad</span>
            <span className={styles.statValue}>{formatPercent(row.grossYield, 1)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
